import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

import { loginUser } from "../../controllers/authController";
import { getCachedRole, getUserProfile, isSignedOutError } from "../../controllers/utilityController";
import api from "../../services/apiService";
import { ROLES } from "../../constants";
import { InputComponent } from "../../components/ui";


const DASHBOARD_BY_ROLE = {
  [ROLES.ADMIN]: "/admin/dashboard",
  [ROLES.HR]: "/hr/dashboard",
  [ROLES.EMPLOYEE]: "/employee/dashboard",
  [ROLES.MANAGER]: "/employee/dashboard",
};

// redirectTo: where to go after signing in instead of the role's dashboard (the chat app passes /chat).
const Login = ({ redirectTo }) => {
  const navigate = useNavigate();
  // Already signed in (a saved token the server still accepts): skip the form. Only a sign-out or a
  // rejected token (401) shows it; if the server can't be reached, the last known role is used.
  const [checkingSession, setCheckingSession] = useState(() => Boolean(localStorage.getItem("token")));

  useEffect(() => {
    if (!localStorage.getItem("token")) return undefined;
    let live = true;
    const forward = (role) => {
      const to = DASHBOARD_BY_ROLE[role] && (redirectTo || DASHBOARD_BY_ROLE[role]);
      if (to) navigate(to, { replace: true });
      else setCheckingSession(false);
    };
    getUserProfile()
      .then((profile) => live && forward(profile?.role))
      .catch((err) => {
        if (!live) return;
        if (isSignedOutError(err)) setCheckingSession(false);
        else forward(getCachedRole());
      });
    return () => {
      live = false;
    };
  }, [navigate, redirectTo]);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();

    if (loading) return;

    setError("");
    setLoading(true);

    try {
      const result = await loginUser(email.trim(), password);

      if (!result?.success || !result?.token) {
        setError(result?.message || "Login failed");
        return;
      }

      localStorage.setItem("token", result.token);
      api.defaults.headers.common["Authorization"] = `Bearer ${result.token}`;

      const profile = await getUserProfile();

      if (redirectTo && Object.values(ROLES).includes(profile?.role)) {
        navigate(redirectTo, { replace: true });
      } else if (profile?.role === ROLES.ADMIN) {
        navigate("/admin/dashboard");
      } else if (profile?.role === ROLES.HR) {
        navigate("/hr/dashboard");
      } else if (profile?.role === ROLES.EMPLOYEE || profile?.role === ROLES.MANAGER) {
        navigate("/employee/dashboard");
      } else {
        setError("Unauthorized role detected");
      }
    } catch (err) {
      setError(err?.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (checkingSession) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-white">
        <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <main className="min-h-screen w-full overflow-hidden bg-white font-sans">
      <div className="grid min-h-screen w-full grid-cols-1 lg:grid-cols-[50%_50%]">
        {/* Left Side */}
        <section className="flex min-h-screen w-full items-center justify-center bg-white px-6 py-10 sm:px-8 lg:px-16">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: "easeOut" }}
            className="w-full max-w-[430px]"
          >
            <div className="mb-9 text-center">
              <img
                src="/assets/adminLogo.png"
                alt="Logo"
                className="mx-auto mb-7 h-14 w-auto object-contain"
              />

              <h1 className="text-3xl font-bold tracking-tight text-gray-900">
                Welcome Back
              </h1>

              <p className="mt-3 text-sm leading-6 text-gray-500">
                {redirectTo
                  ? "Sign in to IRG Chat with your IRG account."
                  : "Please enter your credentials to access the dashboard."}
              </p>
            </div>

            {error && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700"
              >
                {error}
              </motion.div>
            )}

            <form onSubmit={submit} className="space-y-5">
              <InputComponent
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-xl border border-gray-300 text-sm focus:border-[#2570B7] focus:outline-none focus:ring-2 focus:ring-[#2570B7]/20"
                required
              />

              <div>
                <InputComponent
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-xl border border-gray-300 text-sm focus:border-[#2570B7] focus:outline-none focus:ring-2 focus:ring-[#2570B7]/20"
                  required
                />

                <p className="mt-2 text-right text-xs leading-5 text-gray-500">
                  Contact administrator if you forgot your password.
                </p>
              </div>

              <motion.button
                whileHover={!loading ? { y: -1 } : {}}
                whileTap={!loading ? { scale: 0.98 } : {}}
                type="submit"
                disabled={loading}
                className="flex h-12 w-full items-center justify-center rounded-xl bg-[#2570B7] text-sm font-semibold text-white shadow-lg shadow-blue-500/20 transition hover:bg-[#1f5f9d] disabled:cursor-not-allowed disabled:opacity-70"
              >
                {loading ? (
                  <span className="h-5 w-5 animate-spin rounded-lg border-2 border-white border-t-transparent" />
                ) : (
                  "Sign In"
                )}
              </motion.button>
            </form>
          </motion.div>
        </section>

      {/* Right Side */}
<section className="relative hidden min-h-screen overflow-hidden bg-[#f7f9fc] lg:flex items-center justify-center px-12">
  {/* Soft Background Glow */}
  <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(37,112,183,0.12),transparent_45%)]" />

  <motion.div
    initial={{ opacity: 0, scale: 0.96 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.8, ease: "easeOut" }}
    className="relative z-10 max-w-[560px] text-center"
  >
    <motion.p
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2, duration: 0.7, ease: "easeOut" }}
      className="mb-7 text-xs font-semibold uppercase tracking-[0.35em] text-[#2570B7]"
    >
      Technology Partner
    </motion.p>

    <motion.img
      src="/assets/proviloops_logo.png"
      alt="Proviloops International"
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.35, duration: 0.8, ease: "easeOut" }}
      className="mx-auto h-16 w-auto object-contain"
    />

    <motion.p
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.5, duration: 0.8, ease: "easeOut" }}
      className="mx-auto mt-8 max-w-[430px] text-[15px] leading-7 text-gray-500"
    >
      Building refined digital systems, intelligent automation, and scalable
      software experiences for modern businesses.
    </motion.p>

    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.65, duration: 0.8, ease: "easeOut" }}
      className="mx-auto mt-12 h-px w-32 bg-gradient-to-r from-transparent via-[#2570B7]/40 to-transparent"
    />

    <motion.p
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.8, duration: 0.8, ease: "easeOut" }}
      className="mt-8 text-xs font-medium tracking-[0.25em] text-gray-800"
    >
      ENGINEERED WITH PRECISION
    </motion.p>
  </motion.div>
</section>

      </div>
    </main>
  );
};

export default Login;